import { useHttp } from "../http/useHttp";
import {
  DATE_FORMAT_REPORT_FILENAME_DEFAULT,
  dayjs,
  makeFilename,
  useDefaultError,
} from "@synota-io/synota-shared-ui";
import { useAxiosMutation, UseAxiosMutationOptions } from "../http/useAxiosMutation";
import { paths } from "../generated-types";

type ServiceChargePaymentsReportOperation =
  paths["/dashboard/users/{api_token_uuid}/service-charges/payments/reports/{format}"]["post"];
type ServiceChargePaymentsReportRequestBody =
  ServiceChargePaymentsReportOperation["requestBody"]["content"]["application/json"];
type ServiceChargePaymentsReportPath = ServiceChargePaymentsReportOperation["parameters"]["path"];

export type UseServiceChargePaymentsReportInput = ServiceChargePaymentsReportRequestBody & {
  user_uuid: ServiceChargePaymentsReportPath["api_token_uuid"];
};

export const useServiceChargePaymentsReport = (
  input: UseServiceChargePaymentsReportInput,
  options: Omit<
    UseAxiosMutationOptions<Blob, UseServiceChargePaymentsReportInput>,
    "mutationKey" | "mutationFn"
  > = {},
) => {
  const { client } = useHttp();

  const { status, isPending, error, mutate } = useAxiosMutation<
    Blob,
    UseServiceChargePaymentsReportInput
  >({
    mutationKey: ["service-charge-payments-report-csv", input],
    mutationFn: (input) => {
      return client.post(`/users/${input.user_uuid}/service-charges/payments/reports/csv`, input, {
        responseType: "blob",
      });
    },
    ...options,
  });

  useDefaultError(error, "Failed to download payment report, please try again");

  const download = () => {
    mutate(input, {
      onSuccess(data) {
        const blob = data.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          makeFilename(
            "Service Charge Payments",
            dayjs(input.payment_date_start).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT),
            input.payment_date_end
              ? dayjs(input.payment_date_end).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT)
              : undefined,
          )("csv"),
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      },
    });
  };

  return {
    download,
    error,
    isPending,
    status,
  };
};

export type UseServiceChargePaymentsReportReturn = ReturnType<
  typeof useServiceChargePaymentsReport
>;
